/**
 * Time Tracking Service
 * 
 * Handles automatic and manual time tracking with value assessment.
 * Implements robust error handling, offline support, and privacy controls.
 * 
 * @module services/timeTrackingService
 * @author SuperKonnected Team - Senior Dev Implementation
 * @version 1.0.0
 */

import { db } from '../firebase';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  Timestamp,
  limit,
  QueryConstraint
} from 'firebase/firestore';
import {
  TimeTrackingEntry,
  ActivityCategory,
  AppPage,
  TimeOfDay,
  TimeTrackingFilter,
  DateRange,
  TimeBreakdown,
  DailyComparison
} from '../types/timeTracking';

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

const COLLECTION_NAME = 'timeTracking';
const MIN_TRACKING_DURATION = 5; // seconds (filter out accidental clicks)
const MAX_TRACKING_DURATION = 14400; // 4 hours (sanity check)
const BATCH_SAVE_INTERVAL = 30000; // 30 seconds (save batches)
const LOCAL_STORAGE_KEY = 'sk_time_tracking_pending';
const LOCAL_STORAGE_CONFIG_KEY = 'sk_time_tracking_config';

// ============================================================================
// INTERFACES
// ============================================================================

interface CurrentSession {
  category: ActivityCategory;
  activity: string;
  page?: AppPage;
  startTime: Date;
}

interface TrackingConfig {
  enabled: boolean;
  autoTrack: boolean;
  userId: string | null;
}

// ============================================================================
// TIME TRACKING SERVICE (Singleton)
// ============================================================================

class TimeTrackingService {
  private currentSession: CurrentSession | null = null;
  private config: TrackingConfig = {
    enabled: true,
    autoTrack: true,
    userId: null
  };
  private pendingSaves: TimeTrackingEntry[] = [];
  private batchSaveTimer: NodeJS.Timeout | null = null;
  private isInitialized: boolean = false;

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  /**
   * Initialize the tracking service
   * @param userId - Current user ID
   * @param config - Optional configuration
   */
  public initialize(userId: string, config?: Partial<TrackingConfig>): void {
    this.config = {
      ...this.config,
      ...config,
      userId
    };

    // Load any pending saves from localStorage (offline support)
    this.loadPendingSaves();

    // Setup batch save timer
    this.startBatchSaveTimer();

    // Setup beforeunload handler (save on tab close)
    this.setupBeforeUnloadHandler();

    this.isInitialized = true;
    console.log('⏱️ TimeTrackingService initialized for user:', userId);
  }

  /**
   * Cleanup and save any pending data
   */
  public cleanup(): void {
    if (this.currentSession) {
      this.stopTracking('auto');
    }

    this.flushPendingSaves();

    if (this.batchSaveTimer) {
      clearInterval(this.batchSaveTimer);
    }

    this.isInitialized = false;
    console.log('⏱️ TimeTrackingService cleaned up');
  }

  // ============================================================================
  // TRACKING CONTROL
  // ============================================================================

  /**
   * Start tracking time for an activity
   * @param category - Activity category
   * @param activity - Specific activity name
   * @param page - App page (for work activities)
   */
  public startTracking(
    category: ActivityCategory,
    activity: string,
    page?: AppPage
  ): void {
    if (!this.config.enabled || !this.config.userId) {
      console.warn('⏱️ Tracking disabled or user not set');
      return;
    }

    // Stop current session if exists
    if (this.currentSession) {
      this.stopTracking('auto');
    }

    this.currentSession = {
      category,
      activity,
      page,
      startTime: new Date()
    };

    console.log('⏱️ Started tracking:', {
      category,
      activity,
      page,
      time: this.currentSession.startTime.toLocaleTimeString()
    });
  }

  /**
   * Stop current tracking session and save
   * @param source - How the stop was triggered
   */
  public async stopTracking(source: 'auto' | 'manual' = 'auto'): Promise<void> {
    if (!this.currentSession || !this.config.userId) {
      return;
    }

    const endTime = new Date();
    const duration = (endTime.getTime() - this.currentSession.startTime.getTime()) / 1000;

    // Filter out very short sessions (accidental clicks)
    if (duration < MIN_TRACKING_DURATION) {
      console.log('⏱️ Session too short, not saving:', duration, 's');
      this.currentSession = null;
      return;
    }

    // Sanity check: cap at max duration
    const cappedDuration = Math.min(duration, MAX_TRACKING_DURATION);
    if (duration !== cappedDuration) {
      console.warn('⏱️ Duration capped from', duration, 'to', cappedDuration);
    }

    // Create entry
    const entry: TimeTrackingEntry = {
      id: this.generateId(),
      userId: this.config.userId,
      category: this.currentSession.category,
      activity: this.currentSession.activity,
      page: this.currentSession.page,
      startTime: Timestamp.fromDate(this.currentSession.startTime),
      endTime: Timestamp.fromDate(endTime),
      duration: cappedDuration,
      date: this.formatDate(this.currentSession.startTime),
      week: this.getWeekString(this.currentSession.startTime),
      month: this.getMonthString(this.currentSession.startTime),
      dayOfWeek: this.currentSession.startTime.getDay(),
      timeOfDay: this.getTimeOfDay(this.currentSession.startTime),
      source,
      createdAt: Timestamp.now()
    };

    // Add to pending saves (batch processing for performance)
    this.pendingSaves.push(entry);
    this.savePendingToLocalStorage();

    console.log('✅ Tracking stopped:', {
      activity: entry.activity,
      duration: `${Math.round(cappedDuration)}s`,
      pendingCount: this.pendingSaves.length
    });

    this.currentSession = null;

    // If batch is getting large, flush immediately
    if (this.pendingSaves.length >= 10) {
      await this.flushPendingSaves();
    }
  }

  // ============================================================================
  // DATA RETRIEVAL
  // ============================================================================

  /**
   * Get weekly time breakdown
   * @param userId - User ID
   * @param weeksAgo - How many weeks ago (0 = current week)
   * @returns Time breakdown by activity
   */
  public async getWeeklyBreakdown(
    userId: string,
    weeksAgo: number = 0
  ): Promise<{ [category: string]: number }> {
    try {
      const weekString = this.getWeekString(this.getDateWeeksAgo(weeksAgo));

      const q = query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', userId),
        where('week', '==', weekString)
      );

      const snapshot = await getDocs(q);
      const breakdown: { [category: string]: number } = {};

      snapshot.docs.forEach(doc => {
        const data = doc.data() as TimeTrackingEntry;
        const key = `${data.category}_${data.activity}`;
        breakdown[key] = (breakdown[key] || 0) + data.duration;
      });

      console.log('📊 Weekly breakdown loaded:', Object.keys(breakdown).length, 'activities');
      return breakdown;
    } catch (error) {
      console.error('❌ Error loading weekly breakdown:', error);
      return {};
    }
  }

  /**
   * Get daily breakdown for charts
   * @param userId - User ID
   * @param days - Number of days to retrieve
   * @returns Daily comparison data
   */
  public async getDailyBreakdown(
    userId: string,
    days: number = 7
  ): Promise<DailyComparison[]> {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      startDate.setHours(0, 0, 0, 0);

      const q = query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', userId),
        where('startTime', '>=', Timestamp.fromDate(startDate)),
        orderBy('startTime', 'asc')
      );

      const snapshot = await getDocs(q);
      const dailyData: { [date: string]: DailyComparison } = {};

      // Initialize all days
      for (let i = 0; i < days; i++) {
        const date = new Date(startDate);
        date.setDate(date.getDate() + i);
        const dateStr = this.formatDate(date);
        dailyData[dateStr] = {
          date: dateStr,
          dayName: this.getDayName(date),
          work: 0,
          personal: 0,
          self: 0,
          essential: 0,
          totalValue: 0,
          productivity: 0
        };
      }

      // Aggregate data
      snapshot.docs.forEach(doc => {
        const data = doc.data() as TimeTrackingEntry;
        const dateStr = data.date;

        if (dailyData[dateStr]) {
          const hours = data.duration / 3600;
          
          switch (data.category) {
            case 'work':
              dailyData[dateStr].work += hours;
              break;
            case 'personal':
              dailyData[dateStr].personal += hours;
              break;
            case 'self':
              dailyData[dateStr].self += hours;
              break;
            case 'essential':
              dailyData[dateStr].essential += hours;
              break;
          }

          // Add value if available
          if (data.valueAssessment) {
            dailyData[dateStr].totalValue += data.valueAssessment.totalValue;
          }
        }
      });

      const result = Object.values(dailyData).sort((a, b) => 
        a.date.localeCompare(b.date)
      );

      console.log('📊 Daily breakdown loaded:', result.length, 'days');
      return result;
    } catch (error) {
      console.error('❌ Error loading daily breakdown:', error);
      return [];
    }
  }

  /**
   * Get total hours by category for a time period
   * @param userId - User ID
   * @param dateRange - Date range to query
   * @returns Hours by category
   */
  public async getCategoryTotals(
    userId: string,
    dateRange?: DateRange
  ): Promise<{ work: number; personal: number; self: number; essential: number; family: number; activity: number }> {
    try {
      const constraints: QueryConstraint[] = [
        where('userId', '==', userId)
      ];

      if (dateRange) {
        constraints.push(
          where('startTime', '>=', dateRange.start),
          where('startTime', '<=', dateRange.end)
        );
        constraints.push(orderBy('startTime', 'asc'));
      }

      const q = query(collection(db, COLLECTION_NAME), ...constraints);
      const snapshot = await getDocs(q);

      const totals: { work: number; personal: number; self: number; essential: number; family: number; activity: number } = {
        work: 0,
        personal: 0,
        self: 0,
        essential: 0,
        family: 0,
        activity: 0
      };

      snapshot.docs.forEach(doc => {
        const data = doc.data() as TimeTrackingEntry;
        const hours = data.duration / 3600;
        if (data.category in totals) {
          totals[data.category as keyof typeof totals] += hours;
        }
      });

      console.log('📊 Category totals:', totals);
      return totals;
    } catch (error) {
      console.error('❌ Error loading category totals:', error);
      return { work: 0, personal: 0, self: 0, essential: 0, family: 0, activity: 0 };
    }
  }

  // ============================================================================
  // CONFIGURATION
  // ============================================================================

  /**
   * Enable or disable tracking
   * @param enabled - Whether to enable tracking
   */
  public setTrackingEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
    this.saveConfigToLocalStorage();

    if (!enabled && this.currentSession) {
      this.stopTracking('manual');
    }

    console.log('⏱️ Tracking', enabled ? 'enabled' : 'disabled');
  }

  /**
   * Check if tracking is currently enabled
   */
  public isTrackingEnabled(): boolean {
    return this.config.enabled && this.isInitialized;
  }

  // ============================================================================
  // PRIVATE HELPERS
  // ============================================================================

  private generateId(): string {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0]; // YYYY-MM-DD
  }

  private getWeekString(date: Date): string {
    const year = date.getFullYear();
    const week = this.getWeekNumber(date);
    return `${year}-W${week.toString().padStart(2, '0')}`;
  }

  private getMonthString(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
  }

  private getWeekNumber(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  }

  private getTimeOfDay(date: Date): TimeOfDay {
    const hour = date.getHours();
    if (hour >= 6 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 18) return 'afternoon';
    if (hour >= 18 && hour < 22) return 'evening';
    return 'night';
  }

  private getDayName(date: Date): string {
    const days = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];
    return days[date.getDay()];
  }

  private getDateWeeksAgo(weeks: number): Date {
    const date = new Date();
    date.setDate(date.getDate() - (weeks * 7));
    return date;
  }

  private normalizeTimestamp(value: any, fieldName: string): Timestamp {
    if (value instanceof Timestamp) return value;
    if (value instanceof Date) {
      const ms = value.getTime();
      if (!Number.isFinite(ms)) {
        throw new RangeError(`${fieldName} date is invalid`);
      }
      return Timestamp.fromMillis(ms);
    }
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) {
        throw new RangeError(`${fieldName} millis is invalid`);
      }
      return Timestamp.fromMillis(value);
    }
    if (typeof value === 'string') {
      const parsed = new Date(value);
      const ms = parsed.getTime();
      if (!Number.isFinite(ms)) {
        throw new RangeError(`${fieldName} string is invalid`);
      }
      return Timestamp.fromMillis(ms);
    }
    if (value && typeof value === 'object' && typeof value.seconds === 'number') {
      const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : 0;
      return new Timestamp(value.seconds, nanos);
    }

    throw new RangeError(`${fieldName} timestamp is invalid`);
  }

  private toMillis(value: any): number | null {
    try {
      if (value instanceof Timestamp) return value.toMillis();
      if (value instanceof Date) {
        const ms = value.getTime();
        return Number.isFinite(ms) ? ms : null;
      }
      if (typeof value === 'number') return Number.isFinite(value) ? value : null;
      if (typeof value === 'string') {
        const parsed = new Date(value);
        const ms = parsed.getTime();
        return Number.isFinite(ms) ? ms : null;
      }
      if (value && typeof value === 'object' && typeof value.seconds === 'number') {
        const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : 0;
        return value.seconds * 1000 + Math.floor(nanos / 1e6);
      }
      return null;
    } catch {
      return null;
    }
  }

  // ============================================================================
  // BATCH SAVING & OFFLINE SUPPORT
  // ============================================================================

  private startBatchSaveTimer(): void {
    if (this.batchSaveTimer) {
      clearInterval(this.batchSaveTimer);
    }

    this.batchSaveTimer = setInterval(() => {
      if (this.pendingSaves.length > 0) {
        this.flushPendingSaves();
      }
    }, BATCH_SAVE_INTERVAL);
  }

  private async flushPendingSaves(): Promise<void> {
    if (this.pendingSaves.length === 0) return;

    const toSave = [...this.pendingSaves];
    this.pendingSaves = [];


    try {
      // Save to Firestore
      const promises = toSave.map(entry =>
        addDoc(collection(db, COLLECTION_NAME), this.serializeEntry(entry))
      );

      await Promise.all(promises);
      
      console.log('✅ Saved', toSave.length, 'time entries to Firestore');
      
      
      // Clear from localStorage
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    } catch (error) {
      
      console.error('❌ Error saving time entries:', error);
      
      // Put back in queue for retry
      this.pendingSaves = [...toSave, ...this.pendingSaves];
      this.savePendingToLocalStorage();
    }
  }

  private savePendingToLocalStorage(): void {
    try {
      const serialized = this.pendingSaves
        .map(e => this.serializeEntryForStorage(e))
        .filter((e): e is Record<string, any> => e !== null);

      localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify(serialized)
      );
    } catch (error) {
      console.error('❌ Error saving to localStorage:', error);
    }
  }

  private loadPendingSaves(): void {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (stored) {
        const entries = JSON.parse(stored);
        const deserialized: TimeTrackingEntry[] = [];

        entries.forEach((e: any, index: number) => {
          try {
            deserialized.push(this.deserializeEntry(e));
          } catch (err) {
            console.warn('⏱️ Skipping invalid pending entry', { index, err });
          }
        });
        
        
        this.pendingSaves = deserialized;
        console.log('📦 Loaded', this.pendingSaves.length, 'pending saves from localStorage');
        
        // Try to flush immediately
        this.flushPendingSaves();
      }
    } catch (error) {
      console.error('❌ Error loading from localStorage:', error);
    }
  }

  private saveConfigToLocalStorage(): void {
    try {
      localStorage.setItem(LOCAL_STORAGE_CONFIG_KEY, JSON.stringify(this.config));
    } catch (error) {
      console.error('❌ Error saving config to localStorage:', error);
    }
  }

  private setupBeforeUnloadHandler(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('beforeunload', () => {
      if (this.currentSession) {
        this.stopTracking('auto');
      }
      this.flushPendingSaves();
    });
  }

  // ============================================================================
  // SERIALIZATION (Timestamp handling)
  // ============================================================================

  private serializeEntry(entry: TimeTrackingEntry): any {
    
    const result: any = {
      ...entry,
      startTime: entry.startTime,
      endTime: entry.endTime,
      createdAt: entry.createdAt
    };
    
    // Only include updatedAt if it's defined (avoid sending undefined to Firebase)
    if (entry.updatedAt !== undefined) {
      result.updatedAt = entry.updatedAt;
    }
    
    
    return result;
  }

  private serializeEntryForStorage(entry: TimeTrackingEntry): Record<string, any> | null {
    const startMs = this.toMillis(entry.startTime);
    const endMs = this.toMillis(entry.endTime);
    const createdMs = this.toMillis(entry.createdAt);
    const updatedMs = entry.updatedAt !== undefined ? this.toMillis(entry.updatedAt) : null;

    if (startMs === null || endMs === null || createdMs === null) {
      console.warn('⏱️ Skipping pending entry with invalid timestamps', {
        entryId: entry.id,
        startMs,
        endMs,
        createdMs
      });
      return null;
    }

    const serialized: Record<string, any> = {
      ...entry,
      startTime: startMs,
      endTime: endMs,
      createdAt: createdMs
    };

    if (updatedMs !== null) {
      serialized.updatedAt = updatedMs;
    }

    return serialized;
  }

  private deserializeEntry(data: any): TimeTrackingEntry {
    
    const startTime = this.normalizeTimestamp(data.startTime, 'startTime');
    const endTime = this.normalizeTimestamp(data.endTime, 'endTime');
    const createdAt = this.normalizeTimestamp(data.createdAt, 'createdAt');
    const updatedAt = data.updatedAt !== undefined && data.updatedAt !== null
      ? this.normalizeTimestamp(data.updatedAt, 'updatedAt')
      : undefined;

    const result = {
      ...data,
      startTime,
      endTime,
      createdAt,
      updatedAt
    };
    
    
    return result;
  }
}

// ============================================================================
// DOELSTELLINGEN TIME ANALYTICS
// ============================================================================

export interface GoalTimeSummary {
  goalId: string;
  goalTitle: string;
  totalHours: number;
  taskCount: number;
}

/**
 * Get time breakdown per Doelstellingen goal
 * Aggregates timer data from Agenda tasks linked to Doelstellingen actions
 * 
 * @param userId - User ID
 * @param startDate - Start date for aggregation
 * @param endDate - End date for aggregation
 * @returns Array of goal time summaries sorted by total hours (highest first)
 */
export async function getGoalTimeBreakdown(
  userId: string,
  startDate: Date,
  endDate: Date
): Promise<GoalTimeSummary[]> {
  try {
    // 1. Haal alle AgendaTasks op met timer data
    const tasksQuery = query(
      collection(db, 'agendaTasks'),
      where('userId', '==', userId),
      where('listId', '==', 'goals'),
      where('createdAt', '>=', Timestamp.fromDate(startDate)),
      where('createdAt', '<=', Timestamp.fromDate(endDate))
    );
    
    const tasksSnapshot = await getDocs(tasksQuery);
    
    // 2. Haal Doelstellingen data op voor lookup
    const goalsSnapshot = await getDocs(
      query(collection(db, 'goals'), where('ownerId', '==', userId))
    );
    
    const actionsSnapshot = await getDocs(
      query(collection(db, 'actions'), where('ownerId', '==', userId))
    );
    
    // 3. Map task IDs → goal IDs via action link
    const actionGoalMap = new Map<string, string>();
    actionsSnapshot.docs.forEach(doc => {
      const action = doc.data();
      actionGoalMap.set(action.id, action.goalId);
    });
    
    // 4. Aggregate tijd per doel
    const goalTimeMap = new Map<string, { totalMs: number; taskCount: number }>();
    
    tasksSnapshot.docs.forEach(doc => {
      const task = doc.data();
      const goalId = actionGoalMap.get(task.id); // Task ID = Action ID
      
      if (goalId && task.activeTime) {
        const current = goalTimeMap.get(goalId) || { totalMs: 0, taskCount: 0 };
        goalTimeMap.set(goalId, {
          totalMs: current.totalMs + task.activeTime,
          taskCount: current.taskCount + 1
        });
      }
    });
    
    // 5. Return met goal details
    const goalsMap = new Map(
      goalsSnapshot.docs.map(doc => [doc.id, doc.data()])
    );
    
    return Array.from(goalTimeMap.entries())
      .map(([goalId, data]) => ({
        goalId,
        goalTitle: (goalsMap.get(goalId) as any)?.title || 'Onbekend doel',
        totalHours: data.totalMs / (1000 * 60 * 60),
        taskCount: data.taskCount,
      }))
      .sort((a, b) => b.totalHours - a.totalHours); // Hoogste eerst
  } catch (error) {
    console.error('Error fetching goal time breakdown:', error);
    return [];
  }
}

// ============================================================================
// EXPORT SINGLETON INSTANCE
// ============================================================================

export const timeTrackingService = new TimeTrackingService();
export default timeTrackingService;



